<template>
  <div v-if="!HideNavbar" id="navbar-container" class="navbar-container">
    <div
      v-if="!(HideNavbar || HideNavbarOnScroll)"
      class="navbar-content py-2"
      :class="HideNavbar ? 'bg-color-primary-dark' : 'brand-gradient-h'"
    >
      <div class="row-center justify-between px-2 py-1 ms-2">
        <div class="row-center gap-3">
          <!-- Chrome icons are Tabler glyphs, same as everything the menu itself renders. The
               `p-1 -m-1` widens the tap target without changing the bar's height. -->
          <CustomSimpleButton
            v-if="!IsMustChangePasswordActive"
            class="navbar-icon row-center"
            aria-label="Open menu"
            @onClick="onToggleNavbarMenu"
          >
            <i class="ti ti-menu-2 text-l color-light leading-none" aria-hidden="true"></i>
          </CustomSimpleButton>
          <CustomImage
            class="app-logo-navbar cursor-pointer w-auto h-6"
            :src="DashboardLogoWhite"
            :alt="AppName"
            @click="!IsMustChangePasswordActive ? gotoDashboard() : null"
          />
        </div>
        <div class="row-center gap-3 ms-4 me-2">
          <CustomSimpleButton
            v-if="IsNativePlatform && !IsMustChangePasswordActive"
            class="navbar-icon row-center"
            aria-label="QR scanner"
            @onClick="gotoQrScanner"
          >
            <i class="ti ti-scan text-l color-light leading-none" aria-hidden="true"></i>
          </CustomSimpleButton>
          <CustomSimpleButton
            v-if="IsNativePlatform && !IsStudent && !IsMustChangePasswordActive"
            class="navbar-icon row-center"
            aria-label="Offline data"
            @onClick="gotoOfflineData"
          >
            <i class="ti ti-cloud-download text-l color-light leading-none" aria-hidden="true"></i>
          </CustomSimpleButton>
        </div>
      </div>
    </div>
    <TheAffiliationSelector
      v-if="!!currentRoute.meta.showAffiliationSelector && !IsMustChangePasswordActive"
      :isDisabled="
        currentRoute.meta.showAffiliationSelector === AffiliationSelectorStateEnum.DISABLED
      "
      :class="HideNavbarOnScroll ? 'py-1' : ''"
    />
  </div>
</template>

<script lang="ts">
import TheAffiliationSelector from "@/components/features/affiliation/TheAffiliationSelector.vue";
import CustomImage from "@/components/primitives/CustomImage.vue";
import CustomSimpleButton from "@/components/primitives/CustomSimpleButton.vue";
import { AffiliationSelectorStateEnum } from "@/enums/affiliation";
import { gotoDashboard, useAppNavigation } from "@/methods/utils";
import { useParentAffiliationStore } from "@/stores/affiliation/parentAffiliationStore";
import { useAppSettingsStore } from "@/stores/core/appSettingsStore";
import { useSessionStore } from "@/stores/core/sessionStore";
import { IonContent, IonMenu, menuController } from "@ionic/vue";
import { storeToRefs } from "pinia";
import { defineComponent, onBeforeMount, watch } from "vue";
import { useRoute } from "vue-router";

export default defineComponent({
  name: "AppNavbar",
  components: { CustomImage, CustomSimpleButton, IonMenu, IonContent, TheAffiliationSelector },
  setup() {
    const currentRoute = useRoute();
    const appSettingsStore = useAppSettingsStore();
    const {
      AppName,
      DashboardLogoWhite,
      IsNativePlatform,
      IsTabletScreen,
      HideNavbar,
      HideNavbarOnScroll,
    } = storeToRefs(appSettingsStore);
    const sessionStore = useSessionStore();
    const { UserInfo, IsStudent, IsMustChangePasswordActive } = storeToRefs(sessionStore);
    const parentAffiliationStore = useParentAffiliationStore();
    const { initializeAffiliationSelector } = parentAffiliationStore;
    const { ActiveAffiliation, IsSelectorActive } = storeToRefs(parentAffiliationStore);
    const { navigateTo } = useAppNavigation();

    onBeforeMount(async () => {
      // The affiliation-selector load is an authenticated call (adminApi.getAllAffiliations) —
      // while the forced password-change gate is active the backend rejects it (403 → logout
      // cascade), so skip. The navbar remounts via :key="AppMenuKey" when the post-change
      // bootstrap loads the menu, re-running this hook ungated.
      if (UserInfo.value.UserId && !IsMustChangePasswordActive.value) {
        await initializeAffiliationSelector();
      }
    });

    watch(HideNavbar, () => {
      HideNavbarOnScroll.value = HideNavbar.value;
    });

    async function onToggleNavbarMenu() {
      await menuController.toggle("navbar-menu");
    }

    function gotoQrScanner() {
      navigateTo({ name: "QrCodeScanner" });
    }

    function gotoOfflineData() {
      navigateTo({ name: "OfflineDownloads" });
    }
    return {
      IsMustChangePasswordActive,
      AffiliationSelectorStateEnum,
      AppName,
      IsNativePlatform,
      IsTabletScreen,
      HideNavbar,
      IsStudent,
      HideNavbarOnScroll,
      UserInfo,
      DashboardLogoWhite,
      currentRoute,
      ActiveAffiliation,
      IsSelectorActive,
      onToggleNavbarMenu,
      gotoQrScanner,
      gotoOfflineData,
      gotoDashboard,
    };
  },
});
</script>
